import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../../lib/firebase";
import { useAuth } from "../../auth/context/AuthContext";
import { veTodaAgenda } from "../../../shared/utils/permissoesCategoria";

// ============================================
// TIPOS
// ============================================
export interface Agendamento {
  id: string;
  pacienteId: string;
  pacienteNome: string;
  profissionalId: string;
  profissionalNome: string;
  profissionalCategoria: string;
  data: string; // AAAA-MM-DD
  hora: string; // HH:MM
  tipo: "consulta" | "retorno" | "primeira_consulta";
  status:
    | "agendado"
    | "realizado"
    | "nao_realizado"
    | "remarcado"
    | "cancelado";
  observacao?: string;
  // Remarcação
  remarcadoDe?: string; // ID do agendamento original
  remarcadoPara?: string; // ID do novo agendamento
  // Auditoria
  agendadoPor: string;
  agendadoPorNome: string;
  criadoEm?: string;
  atualizadoEm?: string;
}

export interface ConfigAgendaProfissional {
  id: string;
  profissionalId: string;
  profissionalNome: string;
  profissionalCategoria: string;
  duracaoConsulta: number; // minutos (15, 20, 30, 45, 60)
  tipoAgenda: "fixa" | "livre";
  // Para agenda fixa: dias e horários regulares
  diasFixos?: {
    id?: string;
    diaSemana: number; // 0=dom, 1=seg, ..., 6=sab
    horaInicio: string;
    horaFim: string;
  }[];
  // Para agenda livre: apenas duração e limites
  horaInicioDefault?: string;
  horaFimDefault?: string;
  ativo: boolean;
}

export interface DiaExtraAgenda {
  id: string;
  profissionalId: string;
  profissionalNome: string;
  data: string; // AAAA-MM-DD
  horaInicio: string;
  horaFim: string;
  observacao?: string;
  criadoPor: string;
  criadoPorNome: string;
}
export interface AgendaRegular {
  id: string;
  profissionalId: string;
  profissionalNome: string;
  diaSemana: number;
  periodo: "manha" | "tarde" | "integral";
  horaInicio: string;
  horaFim: string;
  ativo: boolean;
}

interface AgendaContextProps {
  agendamentos: Agendamento[];
  agendasRegulares: AgendaRegular[];
  loading: boolean;
  // CRUD Agendamentos
  adicionarAgendamento: (
    dados: Omit<Agendamento, "id" | "criadoEm" | "atualizadoEm">
  ) => Promise<string>;
  atualizarAgendamento: (
    id: string,
    dados: Partial<Agendamento>
  ) => Promise<void>;
  excluirAgendamento: (id: string) => Promise<void>;
  marcarRealizado: (id: string) => Promise<void>;
  marcarNaoRealizado: (id: string) => Promise<void>;
  remarcarAgendamento: (
    id: string,
    novaData: string,
    novaHora: string
  ) => Promise<string>;
  // CRUD Agendas Regulares
  adicionarAgendaRegular: (dados: Omit<AgendaRegular, "id">) => Promise<void>;
  atualizarAgendaRegular: (
    id: string,
    dados: Partial<AgendaRegular>
  ) => Promise<void>;
  excluirAgendaRegular: (id: string) => Promise<void>;
  // Configurações por profissional
  configsProfissionais: ConfigAgendaProfissional[];
  salvarConfigProfissional: (
    dados: Omit<ConfigAgendaProfissional, "id">
  ) => Promise<void>;
  atualizarConfigProfissional: (
    id: string,
    dados: Partial<ConfigAgendaProfissional>
  ) => Promise<void>;
  excluirConfigProfissional: (id: string) => Promise<void>;
  getConfigProfissional: (
    profissionalId: string
  ) => ConfigAgendaProfissional | undefined;
  gerarVagasDia: (profissionalId: string, data: string) => string[];
  // Dias extras
  diasExtras: DiaExtraAgenda[];
  adicionarDiaExtra: (dados: Omit<DiaExtraAgenda, "id">) => Promise<void>;
  excluirDiaExtra: (id: string) => Promise<void>;
  getDiasExtrasProfissional: (
    profissionalId: string,
    mes?: string
  ) => DiaExtraAgenda[];
  // Filtros
  agendamentosPorData: (data: string) => Agendamento[];
  agendamentosPorProfissional: (profissionalId: string) => Agendamento[];
  agendamentosPorPaciente: (pacienteId: string) => Agendamento[];
  agendamentosPorSemana: (dataInicio: string) => Agendamento[];
  agendamentosPorMes: (ano: number, mes: number) => Agendamento[];
}

const AgendaContext = createContext<AgendaContextProps>(
  {} as AgendaContextProps
);
export const useAgenda = () => useContext(AgendaContext);

// ============================================
// PROVIDER
// ============================================
export function AgendaProvider({ children }: { children: ReactNode }) {
  const { usuarioLogado } = useAuth();
  const [agendamentos, setAgendamentos] = useState<Agendamento[]>([]);
  const [agendasRegulares, setAgendasRegulares] = useState<AgendaRegular[]>([]);
  const [diasExtras, setDiasExtras] = useState<DiaExtraAgenda[]>([]);
  const [configsProfissionais, setConfigsProfissionais] = useState<
    ConfigAgendaProfissional[]
  >([]);
  const [loading, setLoading] = useState(true);

  // ============================================
  // CARREGAR AGENDAMENTOS (realtime)
  // ============================================
  useEffect(() => {
    if (!usuarioLogado) return;

    // 🔐 B76 (AI_98): médico (e demais profissionais de agenda própria)
    // recebe da FONTE só os próprios agendamentos — a query espelha a
    // firestore.rule (query sem filtro seria negada). Sem orderBy na
    // variante filtrada (evita índice composto); ordenação no cliente.
    const q = veTodaAgenda(usuarioLogado)
      ? query(collection(db, "agendamentos"), orderBy("data", "asc"))
      : query(
          collection(db, "agendamentos"),
          where("profissionalId", "==", usuarioLogado.id)
        );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const lista: Agendamento[] = snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          pacienteId: data.pacienteId,
          pacienteNome: data.pacienteNome,
          profissionalId: data.profissionalId,
          profissionalNome: data.profissionalNome,
          profissionalCategoria: data.profissionalCategoria || "medico",
          data: data.data,
          hora: data.hora,
          tipo: data.tipo || "consulta",
          status: data.status || "agendado",
          observacao: data.observacao,
          remarcadoDe: data.remarcadoDe,
          remarcadoPara: data.remarcadoPara,
          agendadoPor: data.agendadoPor,
          agendadoPorNome: data.agendadoPorNome,
          modalidade: data.modalidade || "presencial",
          telefoneOnline: data.telefoneOnline || "",
          criadoEm: data.criadoEm?.toDate?.()?.toISOString?.() || data.criadoEm,
          atualizadoEm:
            data.atualizadoEm?.toDate?.()?.toISOString?.() || data.atualizadoEm,
        } as Agendamento;
      });
      // B76: ordenação no cliente (cobre a variante filtrada, sem orderBy)
      lista.sort((a, b) =>
        `${a.data} ${a.hora}`.localeCompare(`${b.data} ${b.hora}`)
      );
      setAgendamentos(lista);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [usuarioLogado]);

  // ============================================
  // CARREGAR AGENDAS REGULARES
  // ============================================
  useEffect(() => {
    if (!usuarioLogado) return;

    // 🔐 B76: mesma lógica dos agendamentos (vide acima)
    const q = veTodaAgenda(usuarioLogado)
      ? query(collection(db, "agendasRegulares"))
      : query(
          collection(db, "agendasRegulares"),
          where("profissionalId", "==", usuarioLogado.id)
        );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const lista: AgendaRegular[] = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as AgendaRegular[];
      setAgendasRegulares(lista);
    });

    return () => unsubscribe();
  }, [usuarioLogado]);
  // ============================================
  // CARREGAR CONFIGURAÇÕES DOS PROFISSIONAIS
  // ============================================
  useEffect(() => {
    if (!usuarioLogado) return;
    // 🔐 B76: mesma lógica dos agendamentos (vide acima)
    const q = veTodaAgenda(usuarioLogado)
      ? query(collection(db, "configsAgenda"))
      : query(
          collection(db, "configsAgenda"),
          where("profissionalId", "==", usuarioLogado.id)
        );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const lista: ConfigAgendaProfissional[] = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as ConfigAgendaProfissional[];
      setConfigsProfissionais(lista);
    });
    return () => unsubscribe();
  }, [usuarioLogado]);

  // ============================================
  // CARREGAR DIAS EXTRAS
  // ============================================
  useEffect(() => {
    if (!usuarioLogado) return;
    // 🔐 B76: mesma lógica dos agendamentos (vide acima)
    const q = veTodaAgenda(usuarioLogado)
      ? query(collection(db, "diasExtrasAgenda"))
      : query(
          collection(db, "diasExtrasAgenda"),
          where("profissionalId", "==", usuarioLogado.id)
        );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const lista: DiaExtraAgenda[] = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as DiaExtraAgenda[];
      setDiasExtras(lista);
    });
    return () => unsubscribe();
  }, [usuarioLogado]);

  // ============================================
  // CRUD DIAS EXTRAS
  // ============================================
  const adicionarDiaExtra = async (dados: Omit<DiaExtraAgenda, "id">) => {
    const dadosLimpos: Record<string, any> = {};
    Object.entries(dados).forEach(([key, value]) => {
      if (value !== undefined) dadosLimpos[key] = value;
    });
    await addDoc(collection(db, "diasExtrasAgenda"), dadosLimpos);
  };

  const excluirDiaExtra = async (id: string) => {
    await deleteDoc(doc(db, "diasExtrasAgenda", id));
  };

  const getDiasExtrasProfissional = (
    profissionalId: string,
    mes?: string
  ): DiaExtraAgenda[] => {
    let lista = diasExtras.filter((d) => d.profissionalId === profissionalId);
    if (mes) {
      lista = lista.filter((d) => d.data.startsWith(mes));
    }
    return lista;
  };

  // ============================================
  // CRUD AGENDAMENTOS
  // ============================================
  const adicionarAgendamento = async (
    dados: Omit<Agendamento, "id" | "criadoEm" | "atualizadoEm">
  ): Promise<string> => {
    // Remover campos undefined (Firebase não aceita)
    const dadosLimpos: Record<string, any> = {};
    Object.entries(dados).forEach(([key, value]) => {
      if (value !== undefined) dadosLimpos[key] = value;
    });
    const docRef = await addDoc(collection(db, "agendamentos"), {
      ...dadosLimpos,
      criadoEm: serverTimestamp(),
      atualizadoEm: serverTimestamp(),
    });
    return docRef.id;
  };

  const atualizarAgendamento = async (
    id: string,
    dados: Partial<Agendamento>
  ) => {
    await updateDoc(doc(db, "agendamentos", id), {
      ...dados,
      atualizadoEm: serverTimestamp(),
    });
  };

  const excluirAgendamento = async (id: string) => {
    await deleteDoc(doc(db, "agendamentos", id));
  };

  const marcarRealizado = async (id: string) => {
    await atualizarAgendamento(id, { status: "realizado" });
  };

  const marcarNaoRealizado = async (id: string) => {
    await atualizarAgendamento(id, { status: "nao_realizado" });
  };

  const remarcarAgendamento = async (
    id: string,
    novaData: string,
    novaHora: string
  ): Promise<string> => {
    const original = agendamentos.find((a) => a.id === id);
    if (!original) throw new Error("Agendamento não encontrado");

    // Criar novo agendamento (sem id e criadoEm do original)
    const novoId = await adicionarAgendamento({
      pacienteId: original.pacienteId,
      pacienteNome: original.pacienteNome,
      profissionalId: original.profissionalId,
      profissionalNome: original.profissionalNome,
      profissionalCategoria: original.profissionalCategoria,
      data: novaData,
      hora: novaHora,
      tipo: original.tipo,
      status: "agendado",
      observacao: original.observacao,
      remarcadoDe: id,
      agendadoPor: usuarioLogado?.id || "",
      agendadoPorNome: usuarioLogado?.nome || "",
    });

    // Atualizar original como remarcado
    await atualizarAgendamento(id, {
      status: "remarcado",
      remarcadoPara: novoId,
    });

    return novoId;
  };

  // ============================================
  // CRUD AGENDAS REGULARES
  // ============================================
  const adicionarAgendaRegular = async (dados: Omit<AgendaRegular, "id">) => {
    await addDoc(collection(db, "agendasRegulares"), dados);
  };

  const atualizarAgendaRegular = async (
    id: string,
    dados: Partial<AgendaRegular>
  ) => {
    await updateDoc(doc(db, "agendasRegulares", id), dados);
  };

  const excluirAgendaRegular = async (id: string) => {
    await deleteDoc(doc(db, "agendasRegulares", id));
  };
  // ============================================
  // CRUD CONFIGURAÇÕES PROFISSIONAIS
  // ============================================
  const salvarConfigProfissional = async (
    dados: Omit<ConfigAgendaProfissional, "id">
  ) => {
    // Verificar se já existe config para este profissional
    const existente = configsProfissionais.find(
      (c) => c.profissionalId === dados.profissionalId
    );
    if (existente) {
      await updateDoc(doc(db, "configsAgenda", existente.id), dados as any);
    } else {
      await addDoc(collection(db, "configsAgenda"), dados);
    }
  };

  const atualizarConfigProfissional = async (
    id: string,
    dados: Partial<ConfigAgendaProfissional>
  ) => {
    await updateDoc(doc(db, "configsAgenda", id), dados as any);
  };

  const excluirConfigProfissional = async (id: string) => {
    await deleteDoc(doc(db, "configsAgenda", id));
  };

  const getConfigProfissional = (profissionalId: string) => {
    return configsProfissionais.find(
      (c) => c.profissionalId === profissionalId
    );
  };

  // Gerar vagas disponíveis para um dia baseado na configuração
  const gerarVagasDia = (profissionalId: string, data: string): string[] => {
    const config = getConfigProfissional(profissionalId);
    if (!config || !config.ativo) return [];
    const dataObj = new Date(data + "T00:00:00");
    const diaSemana = dataObj.getDay();
    let horaInicio = "";
    let horaFim = "";

    // Verificar se há dia extra para esta data
    const diaExtra = diasExtras.filter(
      (d) => d.profissionalId === profissionalId && d.data === data
    );

    if (config.tipoAgenda === "fixa") {
      // Para agenda fixa, buscar TODOS os períodos deste dia da semana
      const periodosNoDia =
        config.diasFixos?.filter((d) => d.diaSemana === diaSemana) || [];

      // Se não é dia fixo E não tem dia extra, sem vagas
      if (periodosNoDia.length === 0 && diaExtra.length === 0) return [];

      // Gerar vagas dos períodos fixos + extras
      const todosPeriodos = [
        ...periodosNoDia.map((p) => ({
          horaInicio: p.horaInicio,
          horaFim: p.horaFim,
        })),
        ...diaExtra.map((d) => ({
          horaInicio: d.horaInicio,
          horaFim: d.horaFim,
        })),
      ];

      const todasVagas: string[] = [];
      todosPeriodos.forEach((periodo) => {
        const [hI, mI] = periodo.horaInicio.split(":").map(Number);
        const [hF, mF] = periodo.horaFim.split(":").map(Number);
        let minutosAtual = hI * 60 + mI;
        const minutosFim = hF * 60 + mF;
        while (minutosAtual + config.duracaoConsulta <= minutosFim) {
          const h = Math.floor(minutosAtual / 60);
          const m = minutosAtual % 60;
          todasVagas.push(
            `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
          );
          minutosAtual += config.duracaoConsulta;
        }
      });

      // Remover vagas já ocupadas
      const agendadosNoDia = agendamentos.filter(
        (a) =>
          a.data === data &&
          a.profissionalId === profissionalId &&
          a.status !== "cancelado"
      );
      return todasVagas.filter(
        (v) => !agendadosNoDia.some((a) => a.hora === v)
      );
    } else {
      // Agenda livre — só gera vagas se tem dia extra aberto para esta data
      if (diaExtra.length === 0) return [];

      // Gerar vagas para cada período extra aberto
      const todasVagas: string[] = [];
      diaExtra.forEach((de) => {
        const [hI, mI] = de.horaInicio.split(":").map(Number);
        const [hF, mF] = de.horaFim.split(":").map(Number);
        let minutosAtual = hI * 60 + mI;
        const minutosFim = hF * 60 + mF;
        while (minutosAtual + config.duracaoConsulta <= minutosFim) {
          const h = Math.floor(minutosAtual / 60);
          const m = minutosAtual % 60;
          todasVagas.push(
            `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
          );
          minutosAtual += config.duracaoConsulta;
        }
      });
      const agendadosNoDia = agendamentos.filter(
        (a) =>
          a.data === data &&
          a.profissionalId === profissionalId &&
          a.status !== "cancelado"
      );
      return todasVagas.filter(
        (v) => !agendadosNoDia.some((a) => a.hora === v)
      );
    }
  };
  // ============================================
  // FILTROS
  // ============================================
  const agendamentosPorData = (data: string): Agendamento[] => {
    return agendamentos.filter(
      (a) => a.data === data && a.status !== "cancelado"
    );
  };

  const agendamentosPorProfissional = (
    profissionalId: string
  ): Agendamento[] => {
    return agendamentos.filter((a) => a.profissionalId === profissionalId);
  };

  const agendamentosPorPaciente = (pacienteId: string): Agendamento[] => {
    return agendamentos.filter((a) => a.pacienteId === pacienteId);
  };

  const agendamentosPorSemana = (dataInicio: string): Agendamento[] => {
    const inicio = new Date(dataInicio + "T00:00:00");
    const fim = new Date(inicio);
    fim.setDate(fim.getDate() + 7);
    const fimStr = fim.toISOString().split("T")[0];

    return agendamentos.filter(
      (a) => a.data >= dataInicio && a.data < fimStr && a.status !== "cancelado"
    );
  };

  const agendamentosPorMes = (ano: number, mes: number): Agendamento[] => {
    const mesStr = `${ano}-${String(mes).padStart(2, "0")}`;
    return agendamentos.filter(
      (a) => a.data.startsWith(mesStr) && a.status !== "cancelado"
    );
  };

  return (
    <AgendaContext.Provider
      value={{
        agendamentos,
        agendasRegulares,
        loading,
        adicionarAgendamento,
        atualizarAgendamento,
        excluirAgendamento,
        marcarRealizado,
        marcarNaoRealizado,
        remarcarAgendamento,
        adicionarAgendaRegular,
        atualizarAgendaRegular,
        excluirAgendaRegular,
        configsProfissionais,
        salvarConfigProfissional,
        atualizarConfigProfissional,
        excluirConfigProfissional,
        getConfigProfissional,
        gerarVagasDia,
        diasExtras,
        adicionarDiaExtra,
        excluirDiaExtra,
        getDiasExtrasProfissional,
        agendamentosPorData,
        agendamentosPorProfissional,
        agendamentosPorPaciente,
        agendamentosPorSemana,
        agendamentosPorMes,
      }}
    >
      {children}
    </AgendaContext.Provider>
  );
}
